import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { format } from 'date-fns';

const MonthlyTrendsChart = ({ data }) => {
  const theme = useTheme();
  
  // Process the data for the chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Sort by month
    return [...data].sort((a, b) => a.month.localeCompare(b.month)).map(item => ({
      month: item.month,
      revenue: item.total_revenue,
      costs: item.total_costs,
      profit: item.profit,
      production: item.energy_production_kwh
    }));
  }, [data]);
  
  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: '#fff', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{formatMonth(label)}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '5px 0', color: entry.color }}>
              {entry.name}: ${entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  // Format month for display
  const formatMonth = (month) => {
    const [year, monthNum] = month.split('-');
    return format(new Date(parseInt(year), parseInt(monthNum) - 1), 'MMM yyyy');
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 40, bottom: 100 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="month" 
          tickFormatter={formatMonth}
          angle={-60}
          textAnchor="end" 
          height={120}
          dy={20}
          tick={{
            fontSize: 16,
            fill: theme.palette.text.secondary
          }}
          interval={0}
        />
        <YAxis 
          yAxisId="left" 
          tickFormatter={(value) => `$${value / 1000}k`}
          label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft', offset: -30 }}
          tick={{
            fontSize: 16,
            dx: -5
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Line 
          yAxisId="left"
          type="monotone" 
          dataKey="revenue" 
          name="Revenue" 
          stroke={theme.palette.primary.main} 
          activeDot={{ r: 8 }}
          strokeWidth={2}
        />
        <Line 
          yAxisId="left"
          type="monotone" 
          dataKey="costs" 
          name="Costs" 
          stroke={theme.palette.error.main} 
          activeDot={{ r: 8 }}
          strokeWidth={2}
        />
        <Line 
          yAxisId="left"
          type="monotone" 
          dataKey="profit" 
          name="Profit" 
          stroke={theme.palette.secondary.main} 
          activeDot={{ r: 8 }}
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default MonthlyTrendsChart;